import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'

// Selectors
import { selectHomeLat, selectHomeLng } from 'src/store/selectors/home'
import { selectHomeValue } from 'src/store/selectors/home/metadata'
import { selectQualifiedEquityListings } from 'src/store/selectors/home/equity'
import { buildViewAllSearchUrl } from 'src/helpers/ylopo'
import { selectYlopoOptIn } from 'src/store/selectors/client'

// Components
import { If, Text, Button } from 'src/Components'
import Format from 'src/Components/shared/Format'
import YlopoListings from '../YlopoListings/YlopoListings'
import { EquitySmartSearchTable } from '../EquitySmartSearchTable'
import { selectShouldRenderEquitySmartSearchYlopoListings } from 'store/selectors/display'
import BotLogo from 'src/Components/shared/Logo/BotLogo'

// constants
import {
  YLOPO_EQUITY_SMART_SEARCH,
  ylopoSearchCriteria
} from 'src/constants/ylopo'

// Utils
import { EquitySmartSearchCalculationClick } from 'src/constants/trackingEvents'
import { OVERLAY_HELPERS } from 'constants/navigation'
import history from 'src/history'

// Styles
import styles from './EquitySmartSearchYlopoOn.module.scss'

const EquitySmartSearchYlopoOn = () => {
  const homeLat = useSelector(selectHomeLat)
  const homeLng = useSelector(selectHomeLng)
  const homeValue = useSelector(selectHomeValue)
  const qualifiedListings = useSelector(selectQualifiedEquityListings)
  const shouldRenderListings = useSelector(
    selectShouldRenderEquitySmartSearchYlopoListings
  )
  const isLoadingListings = qualifiedListings === null

  const clientYlopoOptIn = useSelector(selectYlopoOptIn)
  const clientHasOptedIn = clientYlopoOptIn !== undefined
  const minPriceRange =
    homeValue * ylopoSearchCriteria.SIMILAR_HOME_MIN_PRICE_MULTIPLIER
  const maxPriceRange =
    homeValue * ylopoSearchCriteria.SIMILAR_HOME_MAX_PRICE_MULTIPLIER

  useEffect(() => {
    if (window.YLOPO_WIDGETS.remount) {
      window.YLOPO_WIDGETS.remount({ force: true })
    }
  }, [])

  const dataSearch = {
    circle: {
      lat: homeLat,
      lng: homeLng,
      radius: ylopoSearchCriteria.EQUITY_SMART_SEARCH_SIMILAR_HOME_DISTANCE
    },
    limit: ylopoSearchCriteria.SIMILAR_HOME_LIMIT,
    minPrice: minPriceRange,
    maxPrice: maxPriceRange,
    orderBy: 'sourceCreationDate,desc',
    tag: YLOPO_EQUITY_SMART_SEARCH
  }

  const ylopoSearchUrl = buildViewAllSearchUrl(dataSearch)

  const handleNavigateToCalculation = () => {
    history.push(OVERLAY_HELPERS.equitySmartSearch.buildPath())
  }

  const lastListingsObject = {
    id: 'lastSlide',
    url: ylopoSearchUrl
  }

  return (
    <>
      <div
        style={{ display: 'none' }}
        className='YLOPO_resultsWidget'
        data-search={JSON.stringify(dataSearch)}
      />
      <If condition={!isLoadingListings && !shouldRenderListings}>
        <EquitySmartSearchTable />
      </If>
      <If condition={!isLoadingListings && shouldRenderListings}>
        <div className={styles.headingContainer}>
          <Text
            element='h4'
            color='heather'
            size='xs'
            weight='semibold'
            className={styles.title}
          >
            <FormattedMessage
              id='MarketSpotLight.equitySmartSearch'
              defaultMessage='Equity Smart Search'
            />
          </Text>
          <Text
            element='p'
            size='l'
            align='center'
            paragraph
            className={styles.cta}
          >
            <FormattedMessage
              id='MarketSpotLight.equitySmartSearch.pocket.move'
              defaultMessage='What if you sold your home for {homeValue}, pocketed some equity, and moved to a new home?'
              values={{
                homeValue: (
                  <Text element='span' color='pink'>
                    <Format.Currency value={homeValue} abbreviate />
                  </Text>
                )
              }}
            />
          </Text>
        </div>
        <div
          className={styles.equitySmartSearchYlopoOn}
          data-qa='equitySmartSearchYlopoOn'
        >
          <Button
            className={styles.calculateButton}
            track={EquitySmartSearchCalculationClick}
            onClick={handleNavigateToCalculation}
          >
            <div className={styles.nerdBot}>
              <BotLogo />
            </div>
            <Text className={styles.calculatedText} color='blue'>
              <FormattedMessage
                data-qa='equitySmartSearchCalculationButton'
                id='MarketSpotLight.equitySmartSearch.howIsThisCalculated'
                defaultMessage='How is this calculated?'
              />
            </Text>
          </Button>
          <YlopoListings
            listings={qualifiedListings}
            lastSlide={lastListingsObject}
          />
        </div>
        <Button.YlopoListings
          clientHasOptedIn={clientHasOptedIn}
          className={styles.listingsButton}
          ylopoListingsUrl={ylopoSearchUrl}
        >
          <FormattedMessage
            id='MarketSpotLight.equitySmartSearch.see.all.button'
            defaultMessage='See all homes'
          />
        </Button.YlopoListings>
      </If>
    </>
  )
}

export default EquitySmartSearchYlopoOn
